import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {setHasImage, setIsImageCleared, setIsImageUpdated} from "../../../../slices/dishFormSlice";
import {InformationTooltip} from "../shared-components/InformationTooltip";
import {s3BucketUrl} from "../../../../apiData";
import {InteractiveMenuItemImage} from "../dishes-categories/menu-item/InteractiveMenuItemImage";
import {CloseIcon} from "../../../icons/CloseIcon";

export const FileUploadField = React.memo(({file, setFile}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {id, updated, hasImage, isImageCleared} = useSelector(state => state.dishForm.form);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

    const remoteUrl = useMemo(() => {
        if (!id) return null;
        return `${s3BucketUrl}/menuItems/${id}.webp?t=${updated}`;
    }, [id, updated]);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [file]);

    const handleFileChange = (e) => {
        setErrorMessage(null);
        const newFile = e.target.files?.[0];
        if (!newFile) return;

        if (newFile.size > MAX_FILE_SIZE) {
            e.target.value = "";
            setErrorMessage(t("constraints.fileTooLarge", {size: MAX_FILE_SIZE_MB}));
            return;
        }

        setFile(newFile);
        dispatch(setIsImageCleared(false));
        dispatch(setIsImageUpdated(true));
        dispatch(setHasImage(true));
    };

    const clearFileData = () => {
        setFile(null);
        setPreviewUrl(null);
        dispatch(setHasImage(false));
        dispatch(setIsImageCleared(true));
        dispatch(setIsImageUpdated(false));
    };

    const renderImage = () => {
        if (isImageCleared) return null;

        if (file && previewUrl) {
            return <InteractiveMenuItemImage src={previewUrl} hasImage={true}/>;
        }

        if (!file && hasImage && id && remoteUrl) {
            return <InteractiveMenuItemImage src={remoteUrl} hasImage={true}/>;
        }

        return null;
    };

    const renderButton = () => {
        const hasAnyImage = (!isImageCleared && (hasImage || file));

        if (!hasAnyImage) {
            return (
                <>
                    <label htmlFor={'dish-image'}
                           className={'custom-file-upload label'}>
                        {t('chooseFile')}
                    </label>
                    <input type={'file'}
                           id={'dish-image'}
                           name={'imageName'}
                           onChange={handleFileChange}
                           accept={'.png, .jpg, .jpeg'}
                           className={'file-input'}/>
                </>
            );
        }

        return (
            <div className={'custom-file-upload label clear'}
                 onClick={clearFileData}>
                <span>{t('clear')}</span>
                <CloseIcon/>
            </div>
        );
    };

    return (
        <div className={'form-field-wrapper'}>
            <div className={'form-field-container image'}>
                <label htmlFor={'dish-image'} className={'form-label'}>
                    <InformationTooltip text={t('fileFieldTooltip')}/>
                    {t('image')}:
                </label>
                <div className={'flex-centered'} style={{marginRight: '5px', textWrap: 'nowrap'}}>
                    {renderImage()}
                    <div className={'custom-file-upload'}>
                        {renderButton()}
                    </div>
                </div>
                {errorMessage && <div className={'validation-msg'}>{errorMessage}</div>}
            </div>
        </div>
    );
});